import {
  getRegionCode,
  getRegionName,
  US_STATES,
  US_TERRITORIES,
} from "./state";

const STREET_SUFFIX_PATTERN =
  /^(.*\b(?:avenue|ave|boulevard|blvd|circle|cir|court|ct|drive|dr|highway|hwy|lane|ln|parkway|pkwy|place|pl|road|rd|street|st|trail|trl|way)\.?(?:\s+(?:north|south|east|west|northeast|northwest|southeast|southwest|n|s|e|w|ne|nw|se|sw)\.?)?)\s+(.+)$/i;
const STREET_WITH_UNIT_PATTERN =
  /^(.*\b(?:avenue|ave|boulevard|blvd|circle|cir|court|ct|drive|dr|highway|hwy|lane|ln|parkway|pkwy|place|pl|road|rd|street|st|trail|trl|way)\.?(?:\s+(?:north|south|east|west|northeast|northwest|southeast|southwest|n|s|e|w|ne|nw|se|sw)\.?)?\s+(?:(?:apartment|apt|building|bldg|floor|fl|suite|ste|unit)\.?\s*#?[\w-]+|#\s*[\w-]+))\s+(.+)$/i;
const SAINT_PREFIXED_CITY_PATTERN =
  /^(.*?\b(?:avenue|ave|boulevard|blvd|circle|cir|court|ct|drive|dr|highway|hwy|lane|ln|parkway|pkwy|place|pl|road|rd|street|st|trail|trl|way)\.?(?:\s+(?:north|south|east|west|northeast|northwest|southeast|southwest|n|s|e|w|ne|nw|se|sw)\.?)?)\s+((?:saint|st\.?)\s+.+)$/i;
const US_REGION_PATTERN = [...US_STATES, ...US_TERRITORIES]
  .flatMap((region) => [region.name, region.code])
  .sort((left, right) => right.length - left.length)
  .map(escapePattern)
  .join("|");

function escapePattern(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function matchExpectedCity(
  value: string,
  expectedCity: string | undefined,
): { line1: string; city: string } | null {
  if (!expectedCity) return null;
  const match = value.match(
    new RegExp(`^(.*?)(?:,?\\s+)(${escapePattern(expectedCity)})$`, "i"),
  );
  const line1 = match?.[1].trim();
  return line1 && match ? { line1, city: match[2].trim() } : null;
}

export function hasConflictingTrailingRegion(
  addressPrefix: string,
  zipRegionCode: string,
  parsedCity: string,
): boolean {
  const match = addressPrefix.match(
    new RegExp(`(?:^|[,\\s])(${US_REGION_PATTERN})\\.?$`, "i"),
  );
  const commaCount = addressPrefix.split(",").length - 1;
  if (!match) {
    const segments = addressPrefix.split(",");
    const trailingSegment = segments[segments.length - 1]?.trim() ?? "";
    const trailingToken = trailingSegment.split(/\s+/).pop() ?? "";
    return /^[a-z]{2}\.?$/i.test(trailingToken);
  }
  const typedRegion = match[1];
  const typedRegionCode = getRegionCode(typedRegion);
  const typedRegionIsLocality =
    commaCount <= 1 &&
    parsedCity.localeCompare(typedRegion, undefined, {
      sensitivity: "accent",
    }) === 0;
  return (
    !typedRegionIsLocality && typedRegionCode !== zipRegionCode.toUpperCase()
  );
}

export function parseStreetAndCity(
  addressPrefix: string,
  regionCode: string,
  expectedCity?: string,
): { line1: string; city: string } | null {
  const normalizedExpectedCity = expectedCity?.trim();
  const directExpectedCityMatch =
    normalizedExpectedCity && addressPrefix.split(",").length <= 2
      ? matchExpectedCity(addressPrefix, normalizedExpectedCity)
      : null;
  if (directExpectedCityMatch) return directExpectedCityMatch;
  const regionName = getRegionName(regionCode.toUpperCase());
  const regionPattern = [regionName, regionCode]
    .filter((value): value is string => Boolean(value))
    .sort((left, right) => right.length - left.length)
    .map(escapePattern)
    .join("|");
  const regionMatch = regionPattern
    ? addressPrefix.match(
        new RegExp(`^(.*?)(?:,?\\s+)(${regionPattern})\\.?$`, "i"),
      )
    : null;
  const streetAndCity = (regionMatch?.[1] ?? addressPrefix)
    .replace(/[,\s]+$/, "")
    .trim();
  const expectedCityMatch = matchExpectedCity(
    streetAndCity,
    normalizedExpectedCity,
  );
  if (expectedCityMatch) return expectedCityMatch;
  const lastComma = streetAndCity.lastIndexOf(",");
  if (lastComma >= 0) {
    const line1 = streetAndCity.slice(0, lastComma).trim();
    const city = streetAndCity.slice(lastComma + 1).trim();
    return line1 && city ? { line1, city } : null;
  }

  const streetMatch =
    streetAndCity.match(STREET_WITH_UNIT_PATTERN) ??
    streetAndCity.match(SAINT_PREFIXED_CITY_PATTERN) ??
    streetAndCity.match(STREET_SUFFIX_PATTERN);
  if (!streetMatch) return null;
  const line1 = streetMatch[1].trim();
  const city = streetMatch[2].trim();
  return line1 && city ? { line1, city } : null;
}

export function parseEmbeddedUsStreetAddress(
  value: string | undefined,
  regionCode: string,
  expectedCity?: string,
): { line1: string; city: string } | null {
  const line1 = value?.trim();
  const postalCodeMatch = line1?.match(/\b\d{5}(?:-\d{4})?\s*$/);
  if (!line1 || postalCodeMatch?.index === undefined) return null;
  const addressPrefix = line1
    .slice(0, postalCodeMatch.index)
    .replace(/[,\.\s]+$/, "")
    .trim();
  return addressPrefix
    ? parseStreetAndCity(addressPrefix, regionCode, expectedCity)
    : null;
}
